import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const STORAGE_KEY = "solus_cookie_consent";

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const consent = localStorage.getItem(STORAGE_KEY);
    if (!consent) {
      const timer = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const save = (acceptAll: boolean) => {
    const value = JSON.stringify({
      essential: true,
      analytics: acceptAll ? true : analytics,
      timestamp: new Date().toISOString(),
    });
    localStorage.setItem(STORAGE_KEY, value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50"
      style={{ animation: "slideUp 0.4s ease" }}
      role="dialog"
      aria-label="Aviso de cookies"
    >
      <style>{`
        @keyframes slideUp {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 pb-4">
        <div className="rounded-xl p-5 md:p-6" style={{ background: "#0a0a0a", border: "1px solid rgba(98,169,224,0.25)" }}>
          {!showDetails ? (
            /* Compact view */
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex items-start gap-3 flex-1">
                <div className="w-9 h-9 flex items-center justify-center rounded-full shrink-0" style={{ background: "rgba(98,169,224,0.15)", color: "#62A9E0" }}>
                  <i className="ri-shield-check-line text-base"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-white mb-0.5">Privacidade &amp; Cookies</p>
                  <p className="text-xs text-white/55 leading-relaxed">
                    Usamos cookies para melhorar sua experiência e analisar o tráfego do site.
                    Ao continuar navegando, você concorda com nossa{" "}
                    <Link to="/politica-de-privacidade" className="underline hover:text-[#62A9E0] transition-colors">
                      Política de Privacidade
                    </Link>
                    .
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  onClick={() => setShowDetails(true)}
                  className="text-xs text-white/50 hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap px-3 py-2 rounded-md"
                  style={{ border: "1px solid rgba(255,255,255,0.12)" }}
                >
                  Personalizar
                </button>
                <button
                  onClick={() => save(false)}
                  className="text-xs text-white/70 hover:text-white transition-colors cursor-pointer whitespace-nowrap px-3 py-2 rounded-md"
                  style={{ border: "1px solid rgba(255,255,255,0.18)" }}
                >
                  Rejeitar opcionais
                </button>
                <button
                  onClick={() => save(true)}
                  className="text-xs font-semibold cursor-pointer whitespace-nowrap px-4 py-2 rounded-md solus-btn-primary"
                >
                  Aceitar todos
                </button>
              </div>
            </div>
          ) : (
            /* Detailed view */
            <div>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-bold text-white">Configurar preferências de cookies</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white transition-colors cursor-pointer rounded-md"
                  style={{ border: "1px solid rgba(255,255,255,0.1)" }}
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
              <div className="space-y-3 mb-5">
                {/* Essential */}
                <div className="flex items-start justify-between gap-4 p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.04)" }}>
                  <div>
                    <p className="text-sm font-semibold text-white">Cookies essenciais</p>
                    <p className="text-xs text-white/45 mt-0.5">Necessários para o funcionamento do site. Não podem ser desativados.</p>
                  </div>
                  <div className="shrink-0 mt-0.5">
                    <span className="text-xs font-medium px-2 py-1 rounded-full" style={{ background: "rgba(22,163,74,0.15)", color: "#4ade80" }}>
                      Sempre ativo
                    </span>
                  </div>
                </div>
                {/* Analytics */}
                <div className="flex items-start justify-between gap-4 p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.04)" }}>
                  <div>
                    <p className="text-sm font-semibold text-white">Cookies analíticos</p>
                    <p className="text-xs text-white/45 mt-0.5">Coletam dados de navegação para melhorar a experiência do site.</p>
                  </div>
                  <button
                    onClick={() => setAnalytics(!analytics)}
                    className="shrink-0 mt-0.5 relative w-10 h-5 rounded-full transition-colors cursor-pointer"
                    style={{ background: analytics ? "#62A9E0" : "rgba(255,255,255,0.15)" }}
                    aria-checked={analytics}
                    role="switch"
                  >
                    <span
                      className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform"
                      style={{ transform: analytics ? "translateX(22px)" : "translateX(2px)" }}
                    ></span>
                  </button>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => save(false)}
                  className="flex-1 text-xs text-white/70 hover:text-white transition-colors cursor-pointer whitespace-nowrap py-2 rounded-md"
                  style={{ border: "1px solid rgba(255,255,255,0.18)" }}
                >
                  Salvar preferências
                </button>
                <button
                  onClick={() => save(true)}
                  className="flex-1 text-xs font-semibold cursor-pointer whitespace-nowrap py-2 rounded-md solus-btn-primary"
                >
                  Aceitar todos
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
